import React, { useState, useEffect } from 'react';
import { 
    Dialog, DialogTitle, DialogContent, DialogActions, Button, 
    TextField, MenuItem, Box, Typography, Snackbar, Alert, IconButton, Autocomplete
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete'; // <--- 1. Thêm import icon
import * as API from '../../api';

const SERVICE_CATEGORIES = [
  { code: 'ACCOMMODATION', name: 'Dịch vụ lưu trú (Khách sạn, resort...)' },
  { code: 'TRANSPORT', name: 'Lữ hành & Vận chuyển (Xe, vé...)' },
  { code: 'FNB', name: 'Dịch vụ ăn uống' },
  { code: 'ENTERTAINMENT_SPORTS', name: 'Vui chơi, giải trí & Thể thao' },
  { code: 'SUPPORT', name: 'Hỗ trợ & Thông tin (HDV, Visa...)' },
  { code: 'OTHER', name: 'Dịch vụ bổ trợ (Khác)' }
];

const CustomLabel = ({ title, required = false }) => (
  <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 0.5, color: '#2c3e50', fontSize: '0.9rem' }}>
    {title} {required && <span style={{ color: 'red' }}>*</span>}
  </Typography>
);

const SupplierDialog = ({ open, onClose, initialData, onSuccess }) => {
  const [formData, setFormData] = useState({
    name: '', service_type: 'TOUR', contact_person: '', phone: '', email: '', cc_emails: '', bank_info: '' 
  });
  
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');
  
  // --- 2. Thêm state đánh dấu xóa ảnh ---
  const [deleteQr, setDeleteQr] = useState(false); 

  const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });
  const showToast = (msg, severity = 'success') => setToast({ open: true, message: msg, severity });
  const handleCloseToast = () => setToast({ ...toast, open: false });

    const [locations, setLocations] = useState([]);

  // Thêm useEffect để gọi API lấy danh sách locations
  useEffect(() => {
      const fetchLocations = async () => {
          try {
              const res = await API.client.get('/locations/');
              setLocations(res.data || []);
          } catch (err) {
              console.error("Lỗi tải danh mục địa điểm:", err);
          }
      };
      fetchLocations();
  }, []);

  useEffect(() => {
    if (initialData) {
      setFormData({
          ...initialData,
          cc_emails: initialData.cc_emails || '',
          categories: initialData.categories_detail || [],
            province: initialData.province || '', // Thêm dòng này
          address: initialData.address || ''    // Thêm dòng này
      });
      setPreviewUrl(initialData.payment_qr || ''); 
      setSelectedFile(null);
      setDeleteQr(false); 
    } else {
      setFormData({ name: '', categories: [], contact_person: '', phone: '', email: '', cc_emails: '', bank_info: '', province: '', address: '' });
      setPreviewUrl('');
      setSelectedFile(null);
      setDeleteQr(false);
    }
  }, [initialData, open]);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      setPreviewUrl(URL.createObjectURL(file));
      setDeleteQr(false); // Nếu chọn ảnh mới thì bỏ đánh dấu xóa
    }
  };

  // --- 3. Hàm xử lý khi bấm nút Xóa ảnh ---
  const handleRemoveImage = () => {
      setSelectedFile(null);
      setPreviewUrl('');
      setDeleteQr(true); // Đánh dấu là cần xóa ảnh trên server
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async () => {
    if (!formData.name || formData.name.trim() === '') {
        showToast("Vui lòng nhập Tên Nhà cung cấp!", "error");
        return;
    }

    try {
        const dataToSend = new FormData();

        Object.keys(formData).forEach(key => {
            if (key !== 'payment_qr' && key !== 'categories' && key !== 'categories_detail' && formData[key] !== null && formData[key] !== undefined) {
                dataToSend.append(key, formData[key]);
            }
        });

        if (formData.categories && formData.categories.length > 0) {
            formData.categories.forEach(cat => {
                dataToSend.append('categories', cat.code);
            });
        }

        // --- 4. Logic gửi file hoặc xóa file ---
        if (selectedFile) {
            // Trường hợp 1: Có chọn file mới -> Gửi file lên
            dataToSend.append('payment_qr', selectedFile);
        } else if (deleteQr) {
            // Trường hợp 2: Người dùng bấm xóa -> Gửi chuỗi rỗng để backend xóa file
            dataToSend.append('payment_qr', ''); 
        }

        const config = {
            headers: { 'Content-Type': 'multipart/form-data' },
        };

        if (initialData && initialData.id) {
            await API.updateServiceProvider(initialData.id, dataToSend, config);
        } else {
            await API.createServiceProvider(dataToSend, config);
        }
        
        showToast("Lưu thành công!", "success");
        setTimeout(() => onSuccess(), 500);

    } catch (err) {
        console.error("Lỗi save supplier:", err);
        const resData = err.response?.data;
        let errMsg = err.message;
        
        if (resData) {
             if (typeof resData === 'object') {
                 const details = Object.entries(resData)
                    .map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(', ') : v}`)
                    .join('\n');
                 errMsg = details;
             } else {
                 errMsg = JSON.stringify(resData);
             }
        }
        showToast('Lỗi lưu dữ liệu: ' + errMsg, "error");
    }
  };

  return (
    <>
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ bgcolor: '#007373', color: 'white', fontWeight: 'bold' }}>
        {initialData ? 'CẬP NHẬT NHÀ CUNG CẤP' : 'THÊM MỚI NHÀ CUNG CẤP'}
      </DialogTitle>
      
      <DialogContent dividers>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}> {/* Thêm gap để thoáng hơn */}
            
            <Box>
                <CustomLabel title="Tên Nhà cung cấp" required />
                <TextField name="name" size="small" fullWidth value={formData.name} onChange={handleChange} />
            </Box>

            <Box>
                <CustomLabel title="Danh mục dịch vụ" required/>
                <Autocomplete
                    multiple
                    size="small"
                    options={SERVICE_CATEGORIES}
                    getOptionLabel={(option) => option.name}
                    isOptionEqualToValue={(option, value) => option.code === value.code}
                    value={formData.categories || []}
                    onChange={(e, newValue) => setFormData({ ...formData, categories: newValue })}
                    renderInput={(params) => (
                        <TextField {...params} placeholder="Chọn một hoặc nhiều dịch vụ..." />
                    )}
                />
            </Box>

            <Box sx={{ display: 'flex', gap: 2 }}>
                <Box sx={{ flex: 1 }}>
                    <CustomLabel title="Tỉnh/Thành phố" />
                    <Autocomplete
                        size="small"
                        options={locations}
                        getOptionLabel={(opt) => opt.name || ''}
                        // So sánh theo tên để hiển thị lại giá trị đã lưu
                        value={locations.find(l => l.name === formData.province) || null}
                        onChange={(e, val) => setFormData({ ...formData, province: val ? val.name : '' })}
                        renderInput={(params) => <TextField {...params} placeholder="Chọn tỉnh/thành..." />}
                    />
                </Box>
                <Box sx={{ flex: 2 }}>
                    <CustomLabel title="Địa chỉ chi tiết" />
                    <TextField name="address" size="small" fullWidth placeholder="VD: Số 1, Đường X, Phường Y..." value={formData.address || ''} onChange={handleChange} />
                </Box>
            </Box>

            <Box sx={{ display: 'flex', gap: 2 }}>
                <Box sx={{ flex: 1 }}>
                    <CustomLabel title="Người liên hệ" />
                    <TextField name="contact_person" size="small" fullWidth value={formData.contact_person} onChange={handleChange} />
                </Box>
                <Box sx={{ flex: 1 }}>
                    <CustomLabel title="Số điện thoại" />
                    <TextField name="phone" size="small" fullWidth value={formData.phone} onChange={handleChange} />
                </Box>
            </Box>

            <Box sx={{ display: 'flex', gap: 2 }}>
                <Box sx={{ flex: 1 }}>
                    <CustomLabel title="Email chính (To)" />
                    <TextField name="email" size="small" fullWidth placeholder="nhacungcap@example.com" value={formData.email} onChange={handleChange} />
                </Box>
                <Box sx={{ flex: 1 }}>
                    <CustomLabel title="Email CC" />
                    <TextField name="cc_emails" size="small" fullWidth placeholder="VD: mail1@.com, mail2@.com" value={formData.cc_emails} onChange={handleChange} />
                </Box>
            </Box>
            <Box>
                <CustomLabel title="Thông tin ngân hàng" />
                <TextField name="bank_info" size="small" fullWidth multiline rows={2} placeholder="VD: Vietcombank - 001100xxxxx - NGUYEN VAN A" value={formData.bank_info || ''} onChange={handleChange} />
            </Box>

            {/* --- 5. Giao diện Upload & Preview & Delete --- */}
            <Box sx={{ mt: 1 }}>
                <CustomLabel title="Mã QR Thanh toán" />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1 }}>
                  <Button variant="outlined" component="label" size="small">
                    Tải ảnh lên
                    <input type="file" hidden accept="image/*" onChange={handleFileChange} />
                  </Button>
                  
                  {previewUrl && (
                    <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                        <Box 
                          component="img" 
                          src={previewUrl} 
                          alt="QR Preview" 
                          sx={{ height: 60, width: 'auto', border: '1px solid #ddd', borderRadius: 1 }} 
                        />
                        {/* Nút xóa ảnh */}
                        <IconButton 
                            size="small" 
                            color="error" 
                            onClick={handleRemoveImage}
                            sx={{ 
                                position: 'absolute', 
                                top: -10, 
                                right: -10, 
                                bgcolor: 'white', 
                                border: '1px solid #ddd',
                                '&:hover': { bgcolor: '#ffebee' },
                                width: 24, height: 24
                            }}
                        >
                            <DeleteIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                    </Box>
                  )}
                </Box>
            </Box>

        </Box>
      </DialogContent>
      
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} variant="outlined" color="inherit">Hủy</Button>
        <Button onClick={handleSubmit} variant="contained" sx={{ bgcolor: '#007373' }}>Lưu dữ liệu</Button>
      </DialogActions>
    </Dialog>

    <Snackbar open={toast.open} autoHideDuration={2000} onClose={handleCloseToast} anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
        <Alert onClose={handleCloseToast} severity={toast.severity} sx={{ width: '100%', boxShadow: 3 }} variant="filled">
            {toast.message}
        </Alert>
    </Snackbar>
    </>
  );
};

export default SupplierDialog;